import { useCallback, type FocusEvent, type Ref } from 'react';

import { blurOnEnter } from './blurOnEnter';

export interface InlineEditableLabelInputProps {
  defaultValue: string;
  placeholder?: string;
  variant: 'chip' | 'plain';
  onCommit: (value: string) => void;
  'aria-label'?: string;
  // InputFieldRow imperatively seeds the value when a reference key is chosen, so the input
  // element must stay reachable via ref.
  ref?: Ref<HTMLInputElement>;
}

// Uncontrolled auto-sizing text input for editing a template input's (or its option's) label.
// Commits its value on blur; Enter blurs rather than submitting the surrounding form. The only
// difference between call sites is the skin: a volt tag chip vs. a plain canvas box.
const variantClassName: Record<InlineEditableLabelInputProps['variant'], string> = {
  chip: 'rounded-full border-tag-container-default-volt bg-tag-container-default-volt text-tag-text-default-volt',
  plain: 'rounded-[4px] border-border-default bg-canvas-default text-text-primary',
};

export const InlineEditableLabelInput = ({
  defaultValue,
  placeholder,
  variant,
  onCommit,
  'aria-label': ariaLabel,
  ref,
}: InlineEditableLabelInputProps) => {
  const handleBlur = useCallback((e: FocusEvent<HTMLInputElement>) => onCommit(e.target.value), [onCommit]);

  return (
    <input
      ref={ref}
      type="text"
      defaultValue={defaultValue}
      className={`focus:border-interactive-primary [field-sizing:content] min-w-[120px] border px-12 py-4 leading-none body-300 outline-none ${variantClassName[variant]}`}
      placeholder={placeholder}
      aria-label={ariaLabel}
      onBlur={handleBlur}
      onKeyDown={blurOnEnter}
    />
  );
};
